//  HistoryPage.js - Avec sessions groupées et menu déroulant
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  History,
  Bot,
  User as UserIcon,
  Loader2,
  Droplet,
  Trash2,
  AlertCircle,
  Info,
  Circle,
  HelpCircle,
  HeartPulse,
  ChevronDown,
  ChevronUp,
  MessageSquare,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { historyService } from '../Services/historyService';
import { bloodAnalysisService } from '../Services/bloodAnalysisService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';

// Codes couleurs pour les statuts
const RESULT_COLORS = {
  inRange: {
    hex: '#0D9488',
    text: '#0F766E',
    textDark: '#2DD4BF',
    icon: Circle,
  },
  outOfRange: {
    hex: '#0369A1',
    text: '#075985',
    textDark: '#38BDF8',
    icon: Info,
  },
  unreadable: {
    hex: '#475569',
    text: '#334155',
    textDark: '#94A3B8',
    icon: HelpCircle,
  },
};

// Correspondance balise → catégorie. Les deux vocabulaires cohabitent : le backend produit
// encore l'ancien ([NORMAL]/[SURVEILLER]/[CONSULTATION]), le nouveau est descriptif
// ([DANS_INTERVALLE]/[HORS_INTERVALLE]/[NORME_NON_LISIBLE]). [SURVEILLER] et [CONSULTATION]
// fusionnent : même fait (hors intervalle), ils ne différaient que par une estimation de gravité.
const STATUS_TAGS = {
  '[NORMAL]': 'inRange',
  '[DANS_INTERVALLE]': 'inRange',
  '[SURVEILLER]': 'outOfRange',
  '[CONSULTATION]': 'outOfRange',
  '[HORS_INTERVALLE]': 'outOfRange',
  '[NORME_NON_LISIBLE]': 'unreadable',
};

const safeFormat = (date, fmt, options) => {
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '—';
    return format(d, fmt, options);
  } catch {
    return '—';
  }
};

const HistoryPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [chatSessions, setChatSessions] = useState([]); // 🆕 Sessions groupées
  const [bloodAnalyses, setBloodAnalyses] = useState([]);
  const [loadingChat, setLoadingChat] = useState(true);
  const [loadingBlood, setLoadingBlood] = useState(true);
  const [activeTab, setActiveTab] = useState('chat');
  const [expandedSession, setExpandedSession] = useState(null); // 🆕 Session dépliée
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);

  useEffect(() => {
    fetchChatHistory();
    fetchBloodAnalyses();
  }, [user]);

  // 🆕 Récupérer l'historique GROUPÉ par session
  const fetchChatHistory = async () => {
    try {
      const userId = user?.uid;
      const sessions = await historyService.getHistoryGrouped(userId);
      setChatSessions(sessions);
    } catch (error) {
      console.error('Erreur historique chat:', error);
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoadingChat(false);
    }
  };

  const fetchBloodAnalyses = async () => {
    try {
      const userId = user?.uid;
      if (userId) {
        const analyses = await bloodAnalysisService.getAnalyses(userId);
        setBloodAnalyses(analyses);
      }
    } catch (error) {
      console.error('Erreur historique analyses:', error);
      toast.error('Erreur lors du chargement des analyses');
    } finally {
      setLoadingBlood(false);
    }
  };

  // 🆕 Supprimer une session entière
  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Supprimer cette conversation entière de l'historique ?")) {
      return;
    }

    try {
      const userId = user?.uid;
      await historyService.deleteSession(sessionId, userId);
      setChatSessions((prev) => prev.filter((session) => session.sessionId !== sessionId));
      toast.success('Conversation supprimée');
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAnalysis = async (analysisId) => {
    if (!window.confirm("Supprimer cette analyse de l'historique ?")) {
      return;
    }

    try {
      const userId = user?.uid;
      await bloodAnalysisService.deleteAnalysis(analysisId, userId);
      setBloodAnalyses((prev) => prev.filter((item) => item.id !== analysisId));
      toast.success('Analyse supprimée');
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // 🆕 Toggle session
  const toggleExpandSession = (sessionId) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  const toggleExpandAnalysis = (analysisId) => {
    setExpandedAnalysis(expandedAnalysis === analysisId ? null : analysisId);
  };

  // ==========================================
  // FONCTIONS DE FORMATAGE DES ANALYSES
  // ==========================================

  // Style d'une ligne à partir de SA BALISE UNIQUEMENT (ancien + nouveau vocabulaire).
  // ⚠️ Plus de repli toLowerCase().includes(...) : il classait "vert" toute ligne
  // contenant le mot "normal" (ex. un [CONSULTATION] mentionnant "au-dessus de la normale"),
  // d'où la divergence avec l'écran d'analyse. La balise fait foi.
  const getStatusStyle = (line) => {
    for (const [tag, category] of Object.entries(STATUS_TAGS)) {
      if (line.includes(tag)) return RESULT_COLORS[category];
    }
    return null;
  };

  const cleanStatusTags = (text) => {
    return text
      .replace(/\[[A-Z_]{3,}\]/g, '')
      .replace(/\*\*/g, '')
      .replace(/^[-•*]\s*/, '')
      .replace(/#/g, '')
      .trim();
  };

  const renderAnalysisContent = (analysisText) => {
    if (!analysisText) return null;

    const lines = analysisText.split('\n');
    const renderedElements = [];
    let currentStatusData = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const isStatusLine = Object.keys(STATUS_TAGS).some((tag) => line.includes(tag));

      if (isStatusLine) {
        currentStatusData = getStatusStyle(line);
        const cleanedTitle = cleanStatusTags(line);
        const Icon = currentStatusData.icon;

        renderedElements.push(
          <div
            key={`status-${i}`}
            className="mt-4 p-4 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm border-l-4"
            style={{ borderLeftColor: currentStatusData.hex }}
          >
            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: currentStatusData.hex }}
              >
                <Icon className="w-4 h-4" />
              </div>
              <h4
                className="text-base font-bold"
                style={{
                  color: theme === 'dark' ? currentStatusData.textDark : currentStatusData.text,
                }}
              >
                {cleanedTitle}
              </h4>
            </div>

            <div className="space-y-1.5 pl-10 text-sm text-gray-700 dark:text-gray-300">
              {(() => {
                const subElements = [];
                let j = i + 1;
                while (
                  j < lines.length &&
                  (lines[j].trim().startsWith('-') ||
                    lines[j].trim().startsWith('•') ||
                    lines[j].trim().startsWith('*'))
                ) {
                  let text = lines[j].replace(/^[-•*]\s*/, '');
                  const parts = text.split(/(\*\*.*?\*\*)/).map((part, index) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return (
                        <strong key={index} className="text-gray-900 dark:text-white">
                          {part.slice(2, -2)}
                        </strong>
                      );
                    }
                    return part;
                  });
                  subElements.push(
                    <div key={`bullet-${j}`} className="flex items-start gap-2">
                      <div
                        className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: currentStatusData.hex }}
                      />
                      <p className="leading-relaxed">{parts}</p>
                    </div>
                  );
                  j++;
                }
                i = j - 1;
                return subElements;
              })()}
            </div>
          </div>
        );
      } else if (line.startsWith('###') || (line.startsWith('**') && line.endsWith('**'))) {
        currentStatusData = null;
        const cleanTitle = cleanStatusTags(line.replace(/###/g, ''));
        renderedElements.push(
          <h3
            key={`title-${i}`}
            className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-6 mb-3 flex items-center gap-2"
          >
            <HeartPulse className="w-5 h-5 text-pink-500" />
            {cleanTitle}
          </h3>
        );
      } else if (line.startsWith('-') || line.startsWith('•')) {
        let text = line.replace(/^[-•]\s*/, '');
        const parts = text.split(/(\*\*.*?\*\*)/).map((part, index) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <strong key={index} className="text-gray-900 dark:text-white font-semibold">
                {part.slice(2, -2)}
              </strong>
            );
          }
          return part;
        });

        renderedElements.push(
          <div key={`list-${i}`} className="flex items-start gap-2 my-1.5 ml-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2 flex-shrink-0" />
            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{parts}</p>
          </div>
        );
      } else {
        renderedElements.push(
          <p
            key={`p-${i}`}
            className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed my-2"
          >
            {cleanStatusTags(line)}
          </p>
        );
      }
    }
    return renderedElements;
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="history-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center">
              <History className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground dark:text-gray-100">
                Historique
              </h1>
              <p className="text-muted dark:text-gray-400">
                Retrouvez vos conversations et analyses
              </p>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger
              value="chat"
              className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Bot className="w-4 h-4" />
              Chat IA
              {chatSessions.length > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-white/20 rounded-full">
                  {chatSessions.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="blood"
              className="flex items-center gap-2 data-[state=active]:bg-red-500 data-[state=active]:text-white"
            >
              <Droplet className="w-4 h-4" />
              Analyses de sang
              {bloodAnalyses.length > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-white/20 rounded-full">
                  {bloodAnalyses.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Contenu : Chat IA - 🆕 VERSION GROUPÉE */}
          <TabsContent value="chat">
            {loadingChat ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : chatSessions.length === 0 ? (
              <Card className="p-12 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800 text-center">
                <div className="w-16 h-16 bg-slate-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-muted dark:text-gray-400" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground dark:text-gray-100 mb-2">
                  Aucune conversation
                </h3>
                <p className="text-muted dark:text-gray-400">Vos conversations apparaîtront ici</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {chatSessions.map((session) => (
                  <Card
                    key={session.sessionId}
                    className="relative bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800 hover:border-primary/30 dark:hover:border-primary/50 hover:shadow-md transition-all group overflow-hidden"
                  >
                    {/* Header de session - Cliquable */}
                    <div
                      className="p-6 cursor-pointer"
                      onClick={() => toggleExpandSession(session.sessionId)}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.sessionId);
                        }}
                        className="absolute top-3 right-3 p-2 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Supprimer cette conversation"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-muted dark:text-gray-400 mb-1">
                            {safeFormat(session.firstTimestamp, 'PPp', { locale: fr })}
                          </p>
                          <h3 className="text-lg font-semibold text-foreground dark:text-gray-100 mb-1 truncate">
                            {session.messages[0]?.userMessage || 'Conversation'}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-muted dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {session.messages.length} échange
                              {session.messages.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <p className="text-xs text-primary dark:text-primary/80 mt-2 font-medium flex items-center gap-1">
                            {expandedSession === session.sessionId ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                Réduire
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" />
                                Voir la conversation
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Contenu étendu - Messages de la session */}
                    {expandedSession === session.sessionId && (
                      <div className="px-6 pb-6 pt-0 border-t border-gray-100 dark:border-gray-800">
                        <div className="mt-4 space-y-6">
                          {session.messages.map((message, index) => (
                            <div key={message.id} className="space-y-4">
                              {/* Message utilisateur */}
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-accent dark:bg-accent/80 flex items-center justify-center flex-shrink-0">
                                  <UserIcon className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs text-muted dark:text-gray-500 mb-1">
                                    {safeFormat(message.timestamp, 'HH:mm', { locale: fr })}
                                  </p>
                                  <p className="text-foreground dark:text-gray-100 font-medium bg-gray-50 dark:bg-gray-800 p-3 rounded-xl rounded-tl-none">
                                    {message.userMessage}
                                  </p>
                                </div>
                              </div>

                              {/* Réponse IA */}
                              <div className="flex items-start gap-3 pl-4">
                                <div className="w-8 h-8 rounded-full bg-primary dark:bg-primary/80 flex items-center justify-center flex-shrink-0">
                                  <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                  <div className="prose prose-sm max-w-none dark:prose-invert text-muted bg-blue-50 dark:bg-blue-950/30 p-3 rounded-xl rounded-tl-none">
                                    <ReactMarkdown>{message.aiResponse}</ReactMarkdown>
                                  </div>
                                </div>
                              </div>

                              {/* Séparateur entre les échanges */}
                              {index < session.messages.length - 1 && (
                                <div className="border-t border-dashed border-gray-200 dark:border-gray-700 my-4" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contenu : Analyses de sang */}
          <TabsContent value="blood">
            {loadingBlood ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
              </div>
            ) : bloodAnalyses.length === 0 ? (
              <Card className="p-12 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800 text-center">
                <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Droplet className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground dark:text-gray-100 mb-2">
                  Aucune analyse
                </h3>
                <p className="text-muted dark:text-gray-400">
                  Vos analyses de sang apparaîtront ici
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {bloodAnalyses.map((analysis) => (
                  <Card
                    key={analysis.id}
                    className="relative bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800 hover:border-red-300 dark:hover:border-red-700 hover:shadow-md transition-all group overflow-hidden"
                  >
                    {/* Header cliquable */}
                    <div
                      className="p-6 cursor-pointer"
                      onClick={() => toggleExpandAnalysis(analysis.id)}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAnalysis(analysis.id);
                        }}
                        className="absolute top-3 right-3 p-2 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center flex-shrink-0">
                          <Droplet className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-muted dark:text-gray-400 mb-1">
                            {safeFormat(analysis.timestamp, 'PPp', { locale: fr })}
                          </p>
                          <h3 className="text-lg font-semibold text-foreground dark:text-gray-100 mb-2">
                            Analyse de sang
                          </h3>
                          <p className="text-sm text-muted dark:text-gray-400 line-clamp-2">
                            {analysis.summary}
                          </p>
                          <p className="text-xs text-primary dark:text-primary/80 mt-2 font-medium flex items-center gap-1">
                            {expandedAnalysis === analysis.id ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                Réduire
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" />
                                Voir le détail
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Contenu étendu avec formatage */}
                    {expandedAnalysis === analysis.id && (
                      <div className="px-6 pb-6 pt-0 border-t border-gray-100 dark:border-gray-800">
                        {/* Avertissement médical — identique à l'écran d'analyse
                            (BloodTestAnalysis). Présent ici pour qu'une analyse
                            relue depuis l'historique porte aussi la mention. */}
                        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-400 rounded-r-xl flex gap-3 italic text-sm text-amber-800 dark:text-amber-200">
                          <AlertCircle className="w-5 h-5 flex-shrink-0" />
                          Cette analyse est un outil d'aide à la compréhension. Elle ne constitue pas un diagnostic et ne remplace pas l'avis d'un professionnel de santé. Seul votre médecin peut interpréter ces résultats en tenant compte de votre situation globale.
                        </div>
                        {/* Légende des couleurs (pastilles neutres → interprétation). */}
                        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-600 dark:text-gray-400">
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: RESULT_COLORS.inRange.hex }} />
                            Dans l'intervalle indiqué
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: RESULT_COLORS.outOfRange.hex }} />
                            Hors de l'intervalle indiqué
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: RESULT_COLORS.unreadable.hex }} />
                            Norme non lisible
                          </span>
                        </div>
                        <div className="mt-4">{renderAnalysisContent(analysis.analysisResult)}</div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default HistoryPage;
